import { useEffect, useState } from "react";
import { VisitCounterService } from "../../services/VisitCounterService";
import { PersistantStorage } from "../../Utils/LocalStorage";
import isStandalone from "../../Utils/isStandalone";

// Per-tab guard: a visit is recorded once per browser session. sessionStorage
// survives reloads within a tab but not a new tab, so "once per session" is
// really "once per tab" — which matches the agreed behaviour. The key mirrors
// the backend's expected name so the flag is shared with any other client code.
const VISIT_FLAG_KEY = "dfs_visit_counted";

// 10s read-through cache for the total, backed by localStorage (via
// PersistantStorage's TTL) so it survives a full page reload — react-query's
// in-memory cache would be wiped on F5. Rapid reloads within the window reuse
// the cached value instead of hammering _total.
const CACHE_KEY = "rewampVisitTotal";
const CACHE_TTL_SECONDS = 10;

/**
 * Resolves the site-wide visit total for the footer.
 *
 *   New session   → records the visit (_count returns the updated total),
 *                   seeds the cache, and renders without a second call.
 *   Reload/return → reads _total, serving a <10s cached value first.
 *
 * Returns the total (number) or null while unresolved / on failure, so the
 * footer can fall back to "-" without breaking.
 */
export const useVisitCounter = () => {
  // Seed from the cache so a reload within the window shows the number
  // immediately instead of flashing "-".
  const [total, setTotal] = useState(() => PersistantStorage.get(CACHE_KEY));

  useEffect(() => {
    // The footer renders null in standalone/webview; don't fire any network
    // calls there (hooks still run before that early return).
    if (isStandalone()) return undefined;

    let cancelled = false;
    const cacheAndSet = (count) => {
      if (count == null) return;
      PersistantStorage.set(CACHE_KEY, count, CACHE_TTL_SECONDS);
      if (!cancelled) setTotal(count);
    };

    (async () => {
      const isNewSession = !sessionStorage.getItem(VISIT_FLAG_KEY);

      if (isNewSession) {
        // Record the visit. _count returns the fresh total, which also busts
        // any stale cache the previous session may have left behind.
        const recorded = await VisitCounterService.recordVisit({});
        if (recorded != null) {
          sessionStorage.setItem(VISIT_FLAG_KEY, "1");
          cacheAndSet(recorded);
          return;
        }
        // Increment failed: leave the flag unset so the next load retries, and
        // fall through to the read path to still surface a count.
      }

      // Read path with the 10s cache.
      const cached = PersistantStorage.get(CACHE_KEY);
      if (cached != null) {
        if (!cancelled) setTotal(cached);
        return;
      }
      cacheAndSet(await VisitCounterService.getTotalVisits({}));
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return total;
};
